<template>
  <div>
    <b-row class="match-height">
      <!-- Contact Information: Left col -->
      <b-col
        cols="12"
        xl="6"
        lg="6"
        md="12"
      >
        <b-card class="inquiry-card h-100">
          <!-- Inquiry Header -->
          <div class="inquiry-header">
            <div class="inquiry-salutation">
              {{ inquiryEntry.salutation }}
            </div>
            <h4 class="inquiry-name">
              {{ inquiryEntry.firstName }} {{ inquiryEntry.lastName }}
            </h4>
            <div class="inquiry-meta">
              <div class="inquiry-id">
                <feather-icon
                  icon="HashIcon"
                  size="16"
                />
                Anfrage #{{ inquiryEntry.id }}
              </div>
              <div class="inquiry-email">
                <feather-icon
                  icon="MailIcon"
                  size="16"
                />
                <a
                  :href="`mailto:${inquiryEntry.email}`"
                  class="email-link"
                >
                  {{ inquiryEntry.email }}
                </a>
              </div>
            </div>
          </div>

          <!-- Contact Information -->
          <div class="contact-section">
            <div
              v-if="inquiryEntry.organizationName"
              class="info-row"
            >
              <div class="info-label">
                Unternehmen
              </div>
              <div class="info-value">
                {{ inquiryEntry.organizationName }}
              </div>
            </div>
            
            <div class="info-row">
              <div class="info-label">
                Strasse
              </div>
              <div class="info-value">
                {{ inquiryEntry.street }} {{ inquiryEntry.streetNumber }}
              </div>
            </div>
            
            <div class="info-row">
              <div class="info-label">
                PLZ | Stadt
              </div>
              <div class="info-value">
                {{ inquiryEntry.zipCode }} {{ inquiryEntry.city }}
              </div>
            </div>
            
            <div
              v-if="inquiryEntry.country"
              class="info-row"
            >
              <div class="info-label">
                Land
              </div>
              <div class="info-value">
                {{ inquiryEntry.country }}
              </div>
            </div>

            <div
              v-if="inquiryEntry.phoneNumber"
              class="info-row"
            >
              <div class="info-label">
                Telefon
              </div>
              <div class="info-value">
                <a
                  :href="`tel:${inquiryEntry.phoneNumber}`"
                  class="phone-link"
                >
                  {{ inquiryEntry.phoneNumber }}
                </a>
              </div>
            </div>

            <div
              v-if="inquiryEntry.mobileNumber"
              class="info-row"
            >
              <div class="info-label">
                Mobile
              </div>
              <div class="info-value">
                <a
                  :href="`tel:${inquiryEntry.mobileNumber}`"
                  class="phone-link"
                >
                  {{ inquiryEntry.mobileNumber }}
                </a>
              </div>
            </div>

            <div
              v-if="inquiryEntry.faxNumber"
              class="info-row"
            >
              <div class="info-label">
                Fax
              </div>
              <div class="info-value">
                {{ inquiryEntry.faxNumber }}
              </div>
            </div>
          </div>
        </b-card>
      </b-col>
      
      <!-- Inquiry Status & Details: Right col -->
      <b-col
        cols="12"
        xl="6"
        lg="6"
        md="12"
      >
        <b-card class="status-card h-100">
          <!-- Status Section -->
          <div class="section-header">
            <feather-icon
              icon="ActivityIcon"
              size="20"
              class="section-icon"
            />
            <h5 class="section-title">
              Anfrage Details
            </h5>
          </div>
          
          <div class="status-section">
            <div class="info-row">
              <div class="info-label">
                Bewertung
              </div>
              <div class="info-value">
                <span
                  v-if="inquiryEntry.qualification"
                  class="status-badge"
                >
                  {{ inquiryEntry.qualification }}
                </span>
                <span
                  v-else
                  class="empty-value"
                >Nicht bewertet</span>
              </div>
            </div>

            <!-- Qualification History Timeline -->
            <div
              v-if="inquiryEntry.qualificationHistory && inquiryEntry.qualificationHistory.length > 0"
              class="qualification-history-section"
            >
              <div class="history-header">
                <feather-icon
                  icon="ClockIcon"
                  size="16"
                  class="history-icon"
                />
                <span class="history-title">Qualifizierungs-Historie</span>
              </div>

              <div class="history-timeline">
                <div
                  v-for="(entry, index) in reversedHistory"
                  :key="index"
                  class="timeline-entry"
                >
                  <div class="timeline-dot" />
                  <div class="timeline-content">
                    <div class="entry-header">
                      <span :class="['qualification-badge', getQualificationClass(entry.qualification)]">
                        {{ entry.qualification }}
                      </span>
                      <span class="entry-date">{{ getFormattedDate(entry.at) }}</span>
                    </div>
                    <p
                      v-if="entry.notes"
                      class="entry-notes"
                    >
                      {{ entry.notes }}
                    </p>
                    <p
                      v-else
                      class="entry-notes empty"
                    >
                      Keine Begründung angegeben
                    </p>
                    <small class="entry-meta">{{ entry.by }}</small>
                  </div>
                </div>
              </div>
            </div>

            <div class="info-row">
              <div class="info-label">
                Status
              </div>
              <div class="info-value">
                <span class="status-badge active-badge">
                  {{ inquiry.getStatus(inquiryEntry) }}
                </span>
              </div>
            </div>
            
            <div class="info-row">
              <div class="info-label">
                Erstellt
              </div>
              <div class="info-value">
                {{ getFormattedDate(inquiryEntry.created_at) }}
              </div>
            </div>
            
            <div class="info-row">
              <div class="info-label">
                Leadquelle
              </div>
              <div class="info-value">
                {{ inquiryEntry.leadSource || 'Unbekannt' }}
              </div>
            </div>
          </div>
        </b-card>
      </b-col>
      
      <!-- Description: Full width -->
      <b-col
        cols="12"
        xl="12"
        lg="12"
        md="12"
      >
        <b-card class="message-card">
          <div class="section-header">
            <feather-icon
              icon="MessageSquareIcon"
              size="20"
              class="section-icon"
            />
            <h5 class="section-title">
              Nachricht
            </h5>
          </div>
          <div class="message-content">
            {{ inquiryEntry.message || 'Keine Nachricht verfügbar' }}
          </div>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BCardText,
  BButton,
  BFormCheckbox,
} from 'bootstrap-vue'
import helper from '@/helper/helper'
import Inquiry from '@/store/inquiry/Inquiry'
import inquiriesHelper from '@/helper/inquiriesHelper'

export default {
  setup() {
    const { getFormattedDate } = helper()
    const { isOfferable } = inquiriesHelper()

    return { getFormattedDate, isOfferable }
  },
  components: {
    BCard,
    BRow,
    BCol,
    BCardText,
    BButton,
    BFormCheckbox,
    Inquiry,
  },
  props: {
    inquiryEntry: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      inquiry: Inquiry,
    }
  },
  computed: {
    reversedHistory() {
      if (!this.inquiryEntry.qualificationHistory) return []
      return [...this.inquiryEntry.qualificationHistory].reverse()
    },
  },
  methods: {
    getQualificationClass(qualification) {
      const classMap = {
        Akzeptiert: 'badge-success',
        Abgelehnt: 'badge-danger',
        Sonstiges: 'badge-warning',
        Unbekannt: 'badge-secondary',
      }
      return classMap[qualification] || 'badge-secondary'
    },
  },
}
</script>
<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

* {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.inquiry-card, .status-card, .message-card {
  border: 1px solid #e9ecef;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }
  
  .card-body {
    padding: 2rem;
  }
}

.inquiry-header {
  margin-bottom: 2rem;
  
  .inquiry-salutation {
    font-size: 0.8rem;
    color: #8a92b2;
    text-transform: uppercase;
    letter-spacing: 1px;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }
  
  .inquiry-name {
    font-size: 1.75rem;
    font-weight: 700;
    color: #2c3e50;
    margin-bottom: 1rem;
    line-height: 1.2;
  }
  
  .inquiry-meta {
    .inquiry-id {
      display: inline-flex;
      align-items: center;
      background: #f8f9fa;
      padding: 0.5rem 1rem;
      border-radius: 20px;
      font-size: 0.875rem;
      color: #495057;
      margin-bottom: 0.75rem;
      margin-right: 1rem;
      
      .feather {
        margin-right: 0.5rem;
        color: #6c757d;
      }
    }
    
    .inquiry-email {
      display: flex;
      align-items: center;
      color: #6c757d;
      font-size: 1rem;
      margin-top: 0.25rem;
      
      .feather {
        margin-right: 0.5rem;
        color: #007bff;
      }
      
      .email-link {
        color: #007bff;
        text-decoration: none;
        font-weight: 500;
        
        &:hover {
          color: #0056b3;
          text-decoration: underline;
        }
      }
    }
  }
}

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
  
  .section-icon {
    color: #007bff;
    margin-right: 0.75rem;
  }
  
  .section-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: #2c3e50;
    margin: 0;
  }
}

.contact-section, .status-section {
  .info-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 0.875rem 0;
    border-bottom: 1px solid #f1f3f4;
    
    &:last-child {
      border-bottom: none;
    }
    
    .info-label {
      font-size: 0.875rem;
      color: #8a92b2;
      font-weight: 500;
      min-width: 100px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    
    .info-value {
      font-size: 1rem;
      color: #2c3e50;
      font-weight: 500;
      text-align: right;
      flex: 1;
      
      .phone-link {
        color: #007bff;
        text-decoration: none;
        
        &:hover {
          color: #0056b3;
          text-decoration: underline;
        }
      }
      
      .empty-value {
        color: #adb5bd;
        font-style: italic;
      }
      
      .status-badge {
        display: inline-flex;
        align-items: center;
        padding: 0.375rem 0.75rem;
        border-radius: 20px;
        font-size: 0.875rem;
        font-weight: 500;
        background: #e9ecef;
        color: #495057;
        
        &.active-badge {
          background: #d1ecf1;
          color: #0c5460;
        }
      }
    }
  }
}

.message-content {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  font-size: 1rem;
  line-height: 1.6;
  color: #495057;
  border-left: 4px solid #007bff;
}

.qualification-history-section {
  margin-top: 1.5rem;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #6c757d;

  .history-header {
    display: flex;
    align-items: center;
    margin-bottom: 1rem;

    .history-icon {
      color: #6c757d;
      margin-right: 0.5rem;
    }

    .history-title {
      font-size: 0.875rem;
      font-weight: 600;
      color: #2c3e50;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
  }

  .history-timeline {
    position: relative;
    padding-left: 2rem;
    max-height: 250px;
    overflow-y: auto;
    overflow-x: hidden;
    padding-right: 18px;

    // Custom scrollbar styling
    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 10px;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.3);
      border-radius: 10px;

      &:hover {
        background: rgba(0, 0, 0, 0.5);
      }
    }

    // Vertical line
    &::before {
      content: '';
      position: absolute;
      left: 0.5rem;
      top: 0.5rem;
      bottom: 0.5rem;
      width: 2px;
      background: #dee2e6;
    }

    .timeline-entry {
      position: relative;
      margin-bottom: 1.25rem;
      padding-bottom: 1.25rem;
      border-bottom: 1px solid #e9ecef;

      &:last-child {
        margin-bottom: 0;
        padding-bottom: 0;
        border-bottom: none;
      }

      .timeline-dot {
        position: absolute;
        left: -1.5rem;
        top: 0.25rem;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: #007bff;
        border: 2px solid #f8f9fa;
      }

      .timeline-content {
        .entry-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.5rem;

          .qualification-badge {
            display: inline-block;
            padding: 0.25rem 0.75rem;
            border-radius: 12px;
            font-size: 0.75rem;
            font-weight: 600;

            &.badge-success {
              background: #d4edda;
              color: #155724;
            }

            &.badge-danger {
              background: #f8d7da;
              color: #721c24;
            }

            &.badge-warning {
              background: #fff3cd;
              color: #856404;
            }

            &.badge-secondary {
              background: #e9ecef;
              color: #495057;
            }
          }

          .entry-date {
            font-size: 0.75rem;
            color: #6c757d;
            font-style: italic;
          }
        }

        .entry-notes {
          font-size: 0.875rem;
          line-height: 1.5;
          color: #495057;
          margin-bottom: 0.5rem;
          white-space: pre-wrap;

          &.empty {
            color: #adb5bd;
            font-style: italic;
          }
        }

        .entry-meta {
          font-size: 0.75rem;
          color: #6c757d;
          font-weight: 500;
        }
      }
    }
  }
}
</style>
